'use client';

import { Divider } from '@magicschool/spellbook/components/Divider';
import { HoverCard } from '@magicschool/spellbook/components/HoverCard';
import { Icon, type IconAnimateMode, type IconProps } from '@magicschool/spellbook/components/Icon';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Text } from '@magicschool/spellbook/components/Text';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useAnimatedIcon } from '@magicschool/spellbook/hooks/useAnimatedIcon';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as Collapsible from '@radix-ui/react-collapsible';
import { cva } from 'class-variance-authority';
import { memo, useMemo } from 'react';

export type NavLinkProps = React.ComponentProps<'a'> &
  Pick<Collapsible.CollapsibleProps, 'open' | 'onOpenChange'> & {
    /** Whether the NavLink is currently active */
    active?: boolean;
    /** Animate the icon: `'hover'` plays on hover, `'mount'` plays on mount; `false` explicitly disables. Only valid for supported icon names. */
    animate?: IconAnimateMode;
    /** Custom element type for the link. Pass a routing component like `NextLink` for client-side navigation. */
    element?: React.ElementType;
    /** If false, the NavLink will only display the icon with a Tooltip on hover. */
    expanded?: boolean;
    /** The Lucide Icon name. See https://lucide.dev/ for a list of icons. */
    icon?: IconProps['name'];
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** Accessible label for the expand/collapse chevron button when nested links are present. */
      collapsibleTriggerLabel?: string;
    };
    'data-testid'?: string;
  };

type NavLinkLabelProps = Pick<NavLinkProps, 'animate' | 'children' | 'icon'> & { animating?: boolean };

/**
 * Determine if a NavLink should show aria-current based on its active state or matching nested links
 */
const useAriaCurrent = (
  active?: NavLinkProps['active'],
  href?: NavLinkProps['href'],
  nestedLinks?: React.ReactElement<NavLinkProps> | React.ReactElement<NavLinkProps>[],
): boolean => {
  if (active) return true;
  if (!nestedLinks || !href) return false;

  const links = Array.isArray(nestedLinks) ? nestedLinks : [nestedLinks];
  const activeChild = links.find((child) => child.props.active);

  return activeChild?.props.href === href;
};

const navLinkVariants = cva(
  [
    'text-default-text',
    'cursor-pointer',
    'flex',
    'items-center',
    'w-full',
    'ltr:text-left',
    'rtl:text-right',
    'outline-none',
    'hover:bg-selected-default-background-hover',
    'active:bg-primary-muted-background-hover',
    'focus-visible-ring',
    'transition-colors',
    'py-0',
    'h-9',
    'px-2.5',
    'gap-2.5',
    'shrink-0',
    'rounded-control-sm',
  ],
  {
    variants: {
      active: {
        true: [
          'bg-selected-default-background',
          'text-selected-default-foreground',
          'hover:text-selected-default-foreground-hover',
          'active:text-link-default-active',
          '[&_svg]:text-primary-icon',
        ],
        false: '[&_svg]:text-default-icon',
      },
      hasNestedLinks: {
        true: [
          'items-stretch',
          'p-0',
          'data-[state=open]:bg-transparent',
          'data-[state=open]:hover:bg-selected-default-background-hover',
          'data-[state=open]:active:bg-selected-default-background-active',
          'data-[state=open]:text-default-text',
          'data-[state=open]:[&_svg]:text-default-icon',
        ],
      },
    },
    defaultVariants: {
      hasNestedLinks: false,
      active: false,
    },
  },
);

const navLinkCollapsedVariants = cva(
  [
    'data-[state=open]:bg-primary-muted-background-hover',
    'hover:bg-primary-muted-background-hover',
    'active:bg-primary-muted-background-hover',
    'size-control-h-md',
    'shrink-0',
  ],
  {
    variants: {
      active: {
        true: [
          'bg-selected-default-background',
          'text-selected-default-foreground',
          'hover:text-selected-default-foreground-hover',
          'active:text-link-default-active',
          '[&_svg]:text-primary-icon',
        ],
        false: '',
      },
    },
  },
);

const NavLinkLabel = memo<NavLinkLabelProps>(({ animate, animating, children, icon }) => (
  <>
    {icon && <Icon animate={animate} animating={animating} data-spellbook-name="nav-link-icon" name={icon} size="sm" />}
    <Text className="px-1 leading-5 grow truncate" variant="label-1">
      {children}
    </Text>
  </>
));

const NavLinkButton: React.FC<NavLinkProps> = memo(
  ({ active, animate, children, className, element: Comp = 'a', href, icon, open, onOpenChange, rel, target, text = {}, ...props }) => {
    const { collapsibleTriggerLabel } = text;
    const { slots, rest: label } = useSlots(children, {
      nestedLinks: { Component: NavLink },
    });

    const shouldShowAriaCurrent = useAriaCurrent(Boolean(active), href, slots.nestedLinks);
    const { animating, getInteractProps } = useAnimatedIcon(animate);

    return slots.nestedLinks ? (
      <Collapsible.Root data-spellbook-name="nav-link" className="w-full" open={open} onOpenChange={onOpenChange}>
        <div className={cn(navLinkVariants({ active: Boolean(active), hasNestedLinks: true }), className)} {...getInteractProps()}>
          <Comp
            className="flex items-center grow focus-visible-ring ps-2.5 gap-2.5 rounded-control-sm"
            aria-current={shouldShowAriaCurrent ? 'page' : undefined}
            href={href}
            {...props}
          >
            <NavLinkLabel animate={animate} animating={animating} icon={icon}>
              {label}
            </NavLinkLabel>
          </Comp>
          <Collapsible.Trigger asChild>
            <IconButton
              aria-label={collapsibleTriggerLabel}
              className="data-[state=open]:bg-transparent data-[state=open]:hover:bg-surface-transparent-1 size-control-h-md"
              name={open ? 'chevron-up' : 'chevron-down'}
              color="neutral"
              variant="ghost"
              {...(props['data-testid'] ? { 'data-testid': `${props['data-testid']}-navlink-trigger` } : {})}
            />
          </Collapsible.Trigger>
        </div>
        <Collapsible.Content className="flex w-full py-1 ps-4.25 gap-2 data-[state=open]:animate-collapsible-open data-[state=closed]:animate-collapsible-close">
          <Divider className="!my-0" decorative full={false} orientation="vertical" />
          <div className="flex flex-col shrink gap-1 w-full min-w-0">{slots.nestedLinks}</div>
        </Collapsible.Content>
      </Collapsible.Root>
    ) : (
      <Comp
        data-spellbook-name="nav-link"
        className={cn(navLinkVariants({ active: Boolean(active), hasNestedLinks: false }), className)}
        rel={target === '_blank' ? 'noopener noreferrer' : rel}
        target={target}
        aria-current={shouldShowAriaCurrent ? 'page' : undefined}
        href={href}
        {...props}
        {...getInteractProps(props)}
      >
        <NavLinkLabel animate={animate} animating={animating} icon={icon}>
          {label}
        </NavLinkLabel>
      </Comp>
    );
  },
);

export const NavLinkIconButton: React.FC<NavLinkProps> = memo(
  ({ active, animate, children, element: Comp = 'a', href, icon, onClick, ...props }) => {
    const { slots, rest: label } = useSlots(children, {
      nestedLinks: { Component: NavLink },
    });

    const shouldShowAriaCurrent = useAriaCurrent(Boolean(active), href, slots.nestedLinks);
    const labelId = useId();
    const isNamedByCaller = Boolean(props['aria-label'] ?? props['aria-labelledby']);
    const fallbackLabelId = isNamedByCaller ? undefined : labelId;
    // biome-ignore lint: trigger intentionally only recomputes when rendered link inputs change.
    const Trigger = useMemo(
      () => (
        <IconButton
          asChild
          animate={animate}
          className={navLinkCollapsedVariants({ active: Boolean(active) })}
          name={icon}
          onClick={onClick as React.MouseEventHandler}
          color="neutral"
          variant="ghost"
        >
          <Comp {...props} href={href} aria-current={shouldShowAriaCurrent ? 'page' : undefined} />
        </IconButton>
      ),
      [active, animate, icon, onClick, props, href, shouldShowAriaCurrent],
    );

    return slots.nestedLinks ? (
      <>
        {/* The HoverCard renders its label only while open, so the closed icon needs its own name. */}
        {fallbackLabelId && (
          <span id={fallbackLabelId} hidden>
            {label}
          </span>
        )}
        <HoverCard
          align="start"
          alignOffset={-40}
          className="bg-surface-background-1 ltr:rounded-l-none rtl:rounded-r-none p-2 pt-4"
          closeDelay={150}
          openDelay={150}
          side="right"
          sideOffset={12}
        >
          <HoverCard.Trigger asChild aria-labelledby={fallbackLabelId}>
            {Trigger}
          </HoverCard.Trigger>
          <HoverCard.Content>
            <div className="flex flex-col gap-1 w-48">
              <Text className="pl-4 mb-1" color="default-soft" variant="caption-1">
                {label}
              </Text>
              {slots.nestedLinks}
            </div>
          </HoverCard.Content>
        </HoverCard>
      </>
    ) : (
      <Tooltip delayDuration={150} mode={isNamedByCaller ? 'description' : 'label'} side="right">
        <Tooltip.Trigger>{Trigger}</Tooltip.Trigger>
        <Tooltip.Content>{label}</Tooltip.Content>
      </Tooltip>
    );
  },
);

/**
 * Navigation link for sidebar menus that supports icons, active state, collapsible nested links, and a collapsed icon-only mode.
 *
 * ### When to use
 * - For primary and secondary navigation items in sidebars
 * - When navigation items need an icon, active state indicator, or collapsible children
 * - For responsive sidebars that collapse to icon-only mode
 *
 * ### When NOT to use
 * - For inline text links: use `Link` instead
 * - For standalone buttons or actions: use `Button` or `IconButton` instead
 * - For tab-style navigation: use `Tabs` instead
 *
 * ### API
 * - `active`: Highlights the link with active styling and sets `aria-current="page"`
 * - `icon`: Lucide icon name displayed before the label
 * - `expanded`: When `false`, renders as an icon-only `IconButton` with a `Tooltip` (or `HoverCard` when nested links exist)
 * - `element`: Custom element type for routing integration (e.g., pass `NextLink` for client-side navigation)
 * - `open`, `onOpenChange`: Controls the collapsible state when the NavLink has nested children
 * - `text.collapsibleTriggerLabel`: Accessible label for the expand/collapse chevron button
 *
 * ### Composition
 * - Nest `<NavLink>` elements as children to create collapsible sub-navigation
 * - When expanded, nested links render in a collapsible section with a vertical divider
 * - When collapsed (`expanded={false}`), nested links appear in a `HoverCard` on hover
 *
 * ### Visual states
 * - **Default**: Neutral text and icon color
 * - **Hover**: Primary muted background
 * - **Active**: Primary muted background with link-colored text and primary icon
 * - **Focus**: Focus ring via `focus-visible` styles
 * - **Collapsed**: Renders as an `IconButton` with `Tooltip` or `HoverCard` for nested links
 *
 * ### Accessibility
 * - Sets `aria-current="page"` when `active` or when a nested link matching the parent `href` is active
 * - Adds `rel="noopener noreferrer"` automatically for `target="_blank"` links
 * - Provide `text.collapsibleTriggerLabel` for the expand/collapse button when using nested links
 * - When `expanded={false}`, the icon-only link takes its accessible name from its label unless you pass `aria-label` or `aria-labelledby`
 * - **Caveat**: When `expanded={false}` and the NavLink has nested links, the sub-navigation is rendered inside a `HoverCard`, which is invisible to screen readers and unreachable by keyboard. Nested links in the collapsed state are only accessible to sighted mouse users.
 */
export const NavLink: React.FC<NavLinkProps> = memo(({ expanded = true, onOpenChange, ...props }) => {
  return expanded ? <NavLinkButton onOpenChange={onOpenChange} {...props} /> : <NavLinkIconButton {...props} />;
});
